<template>
  <section class="py-9 tablet:py-20">
    <div class="container-wrap">
      <h2 class="text-h2 mb-10 tablet:line-clamp-2">
        {{ title }}
      </h2>
      <TheRichText
        v-if="descriptionHtml || description"
        class="mb-10 w-full text-sm leading-6 text-gray-700 tablet:line-clamp-7 tablet:text-base"
        :text="descriptionHtml || description"
        :is-html="!!descriptionHtml"
      />

      <ul
        class="tablet:grid tablet:grid-cols-3 tablet:grid-rows-2 tablet:gap-5"
      >
        <li
          v-if="items[0]"
          class="mb-4 tablet:col-span-2 tablet:row-span-2 tablet:mb-0"
        >
          <TheNuxtLink
            :id="items[0].trackingCode"
            :to="items[0].link"
            :title="items[0].name"
            :class="[itemHoverClass, firstItemClass]"
            :style="contextThumbnailStyle"
            class="relative block aspect-video overflow-hidden rounded-3xl bg-cover bg-center bg-no-repeat transition-shadow before:absolute before:left-0 before:top-0 before:block before:h-full before:w-full before:bg-gradient-to-tr before:via-transparent before:content-[''] tablet:aspect-auto tablet:h-full tablet:w-full"
          >
            <div
              class="relative z-10 flex h-full w-full flex-col justify-end p-4 tablet:p-5"
            >
              <h3 class="text-h5 mb-2 truncate text-white">
                {{ items[0].name }}
              </h3>
              <p class="flex items-center text-white">
                <span
                  v-if="items[0].linkName"
                  class="mr-1 truncate text-sm leading-6 underline"
                >
                  {{ items[0].linkName }}
                </span>
                <IconArrow
                  direction="right"
                  class="h-6 w-6 flex-shrink-0 basis-6"
                />
              </p>
            </div>
          </TheNuxtLink>
        </li>
        <li v-if="items[1]" class="mb-4 tablet:mb-0">
          <TheNuxtLink
            :id="items[1].trackingCode"
            :to="items[1].link"
            :title="items[1].name"
            :class="[itemHoverClass]"
            class="block overflow-hidden rounded-3xl bg-black transition-shadow tablet:aspect-video"
          >
            <div class="flex h-full w-full flex-col justify-end p-4 tablet:p-5">
              <h3 class="text-h5 mb-2 truncate text-white">
                {{ items[1].name }}
              </h3>
              <p class="flex items-center text-white">
                <span
                  v-if="items[1].linkName"
                  class="mr-1 truncate text-sm leading-6 underline desktop:hidden"
                >
                  {{ items[1].linkName }}
                </span>
                <IconArrow
                  direction="right"
                  class="h-6 w-6 flex-shrink-0 basis-6"
                />
              </p>
            </div>
          </TheNuxtLink>
        </li>
        <li v-if="items[2]" class="mb-4 tablet:mb-0">
          <TheNuxtLink
            :id="items[2].trackingCode"
            :to="items[2].link"
            :title="items[2].name"
            :class="[itemHoverClass, thirdItemClass]"
            class="block overflow-hidden rounded-3xl transition-shadow tablet:aspect-video"
          >
            <div class="flex h-full w-full flex-col justify-end p-4 tablet:p-5">
              <h3 class="text-h5 mb-2 truncate text-white">
                {{ items[2].name }}
              </h3>
              <p class="flex items-center text-white">
                <span
                  v-if="items[2].linkName"
                  class="mr-1 truncate text-sm leading-6 underline desktop:hidden"
                >
                  {{ items[2].linkName }}
                </span>
                <IconArrow
                  direction="right"
                  class="h-6 w-6 flex-shrink-0 basis-6"
                />
              </p>
            </div>
          </TheNuxtLink>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
  /**
   * @see https://www.figma.com/design/QJ3ZvEbUHI9xI11iudYf8U/Advantech-WISE-Library?node-id=12864-27493&t=89PIFDiQEC4zcSbf-0
   */

  interface IItem {
    name: string;
    link: string;
    linkName?: string;
    trackingCode?: string;
  }

  interface IProps {
    title: string;
    description?: string;
    descriptionHtml?: string;
    thumbnail?: string;
    items: IItem[];
    theme?: TTheme;
  }

  const props = withDefaults(defineProps<IProps>(), {
    description: undefined,
    descriptionHtml: undefined,
    thumbnail: undefined,
    theme: 'blue',
  });

  const contextThumbnailStyle = computed((): string => {
    if (!props.thumbnail) return '';
    return `background-image: url(${props.thumbnail});`;
  });

  const firstItemClass = computed((): string => {
    switch (props.theme) {
      case 'green':
        return 'before:from-green-500 before:to-green-500';
      case 'cyan':
        return 'before:from-cyan-500 before:to-cyan-500';
      case 'purple':
        return 'before:from-purple-500 before:to-purple-500';
      case 'yellow':
        return 'before:from-yellow-500 before:to-yellow-500';
      default:
        return 'before:from-blue-500 before:to-blue-500';
    }
  });

  const thirdItemClass = computed((): string => {
    switch (props.theme) {
      case 'green':
        return 'bg-green-400';
      case 'cyan':
        return 'bg-cyan-400';
      case 'purple':
        return 'bg-purple-400';
      case 'yellow':
        return 'bg-yellow-400';
      default:
        return 'bg-blue-400';
    }
  });

  const itemHoverClass = computed((): string => {
    switch (props.theme) {
      case 'green':
        return 'hover:shadow-button hover:shadow-green-400';
      case 'cyan':
        return 'hover:shadow-button hover:shadow-cyan-400';
      case 'purple':
        return 'hover:shadow-button hover:shadow-purple-400';
      case 'yellow':
        return 'hover:shadow-button hover:shadow-yellow-400';
      default:
        return 'hover:shadow-button hover:shadow-blue-400';
    }
  });

  export type { IItem };
</script>
